<template>
  <div class="app-container" :class="{ 'cyber-page': isCyberLayout }">
    <!-- 导航栏 - 根据路由切换样式 -->
    <CyberNavBar
      v-if="isCyberPage"
    />
    <NavBar
      v-else
      v-model:showLogin="showLogin"
      v-model:showContact="showContact"
      v-model:showApiConfig="showApiConfig"
    />

    <!-- 路由视图 -->
    <router-view />

    <!-- 联系对话框 -->
    <el-dialog
      v-model="showContact"
      :show-header="false"
      width="500px"
      center
      align-center
      class="read-me-dialog"
      modal-class="read-me-overlay"
      append-to-body
    >
      <div class="contact-content">
        <div class="dialog-title">元译科技团队——专注翻译技术</div>
        <div class="info-grid">
          <div class="info-section feature">
            <h3>笔记添加</h3>
            <p>●左键选择文本<br>
               ●右键添加到笔记（Alt+E/C）</p>
          </div>
          
          <div class="info-section feature">
            <h3>使用说明</h3>
            <p>●推荐使用Chrome浏览器<br>
               ●页面缩放100%为宜<br>
               ●手动缩放页面，请先展开"右侧栏"</p>
          </div>
          
          <div class="info-section warning">
            <h3>免责声明</h3>
            <p>●素材源于网络，仅供参考<br>
               ●相关数据缓存在您自己的设备上，请自行备份一份</p>
          </div>
          
          <div class="info-section contact">
            <h3>联系方式</h3>
            <div class="contact-grid">
              <div class="contact-item">
                <p class="email">元译科技-微信公众号</p>
              </div>
              <div class="contact-item qr-code">
                <div class="qr-placeholder">
                  <span>二维码</span>
                </div>
              </div>
              <div class="contact-item">
                <p class="email">meta_translate@126.com</p>
              </div>
              <div class="contact-item">
                <p class="email">meta_translate@163.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- API配置对话框 -->
    <ApiConfig v-model="showApiConfig" />

    <!-- AI结果对话框 -->
    <AIResultDialog
      v-model:visible="aiDialogVisible"
      :title="aiDialogTitle"
      :content="aiDialogContent"
    />

    <!-- 笔记对话框 -->
    <NotesDialog />

    </div>
</template>

<script>
import { ref, onMounted, computed, onBeforeUnmount, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { getSupabase } from '@/config/localStorage'
import NavBar from './components/NavBar.vue'
import CyberNavBar from './components/CyberNavBar.vue'
import ApiConfig from './components/ApiConfig.vue'
import AIResultDialog from './components/AIResultDialog.vue'
import NotesDialog from './components/NotesDialog.vue'
import './styles/main.css'
import { ElMessage } from 'element-plus'
import request from '@/utils/request'

export default {
  name: 'App',
  components: {
    NavBar,
    CyberNavBar,
    ApiConfig,
    AIResultDialog,
    NotesDialog
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const showContact = ref(false)
    const showApiConfig = ref(false)

    // 导航栏与容器布局由路由的 meta 声明（见 router/index.js），
    // 不再用路径字符串精确匹配 —— 那样 /xxx/ 这类带尾斜杠的 URL 会匹配失败，
    // 导致 CATTI 导航栏错误地出现在赛博风页面上。
    // Route meta is the source of truth after navigation resolves. The path
    // fallback keeps the first dev/HMR frame from briefly rendering CATTI layout.
    const cyberNavbarPaths = new Set([
      '/',
      '/translation-tool',
      '/translation-services',
      '/api-tutorial',
      '/terminology-tool',
      '/word-count-tool',
      '/word-frequency-tool',
      '/corpus-alignment-tool'
    ])
    const cyberLayoutPaths = new Set([
      '/',
      '/translation-tool',
      '/translation-services',
      '/api-tutorial',
      '/terminology-tool',
      '/word-count-tool',
      '/word-frequency-tool',
      '/corpus-alignment-tool'
    ])
    const normalizedRoutePath = computed(() => route.path.replace(/\/+$/, '') || '/')
    const isCyberPage = computed(() => route.meta.navbar === 'cyber' || cyberNavbarPaths.has(normalizedRoutePath.value))
    const isCyberLayout = computed(() => route.meta.layout === 'cyber' || cyberLayoutPaths.has(normalizedRoutePath.value))

    const currentFile = computed(() => store.state.currentFile)
    const aiDialogVisible = computed({
      get: () => store.state.aiDialogVisible,
      set: (value) => store.commit('setAiDialogVisible', value)
    })
    const aiDialogTitle = computed(() => store.state.aiDialogTitle)
    const aiDialogContent = computed(() => store.state.aiDialogContent)
    const resetPopupBodyState = () => {
      document.body.classList.remove('el-popup-parent--hidden')
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('padding-right')
    }

    const hasVisiblePopupContent = (node) => {
      const popup = node.querySelector('.el-dialog, .el-message-box')
      if (!popup) return false

      const style = window.getComputedStyle(popup)
      const opacity = Number(style.opacity)
      return (
        popup.getClientRects().length > 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        (Number.isNaN(opacity) || opacity > 0)
      )
    }

    const removeStalePopupNodes = () => {
      document.querySelectorAll('.el-loading-mask').forEach((node) => {
        node.remove()
      })
      document.querySelectorAll('.el-overlay').forEach((node) => {
        if (!hasVisiblePopupContent(node)) {
          node.remove()
        }
      })
    }

    const closeTransientDialogs = () => {
      showContact.value = false
      showApiConfig.value = false
      store.commit('setAiDialogVisible', false)
      store.commit('setNoteDialogVisible', false)
      removeStalePopupNodes()
      resetPopupBodyState()
    }

    
    // 添加屏幕唤醒锁定功能相关变量
    const wakeLock = ref(null)
    const isFullscreen = ref(false)

    // 监听键盘快捷键
    const handleKeyDown = (event) => {
      // 管理员用户不启用笔记相关快捷键
      const isAdmin = store.state.isAdmin;

      // Alt+C 打开汉译英笔记
      if (event.altKey && event.key === 'c' && !isAdmin) {
        event.preventDefault()
        event.stopPropagation()
        const text = window.getSelection().toString().trim()
        if (text) {
          store.dispatch('openChineseToEnglishNotes', text)
        }
      }
      // Alt+E 打开英译汉笔记
      else if (event.altKey && event.key === 'e' && !isAdmin) {
        event.preventDefault()
        event.stopPropagation()
        const text = window.getSelection().toString().trim()
        if (text) {
          store.dispatch('openEnglishToChineseNotes', text)
        }
      }
      // Ctrl+S 保存内容
      else if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        // 始终阻止浏览器的默认保存行为
        event.preventDefault()
        event.stopPropagation()
        
        // 检查当前焦点是否在 AIResultDialog 的文本框中
        const activeElement = document.activeElement;
        const isInAIDialog = activeElement && activeElement.closest('.ai-result-dialog');
        
        if (isInAIDialog) {
          // 如果在 AIResultDialog 中，触发其保存功能
          const aiDialog = document.querySelector('.ai-result-dialog');
          if (aiDialog) {
            const saveButton = aiDialog.querySelector('.el-button--primary');
            if (saveButton) {
              saveButton.click();
            }
          }
        } else if (store.state.currentFile) {
          // 如果在主编辑区域，保存翻译内容
          import('@/service/file').then(module => {
            const FileService = module.FileService
            // 保存自译内容而不是机器翻译内容
            const selfTranslationContent = store.state.selfTranslation || ''
            FileService.handleSave(store, selfTranslationContent)
              .then(() => {
                console.log('快捷键保存成功')
                ElMessage.success('自译内容保存成功')
              })
              .catch(error => {
                console.error('快捷键保存失败:', error)
                ElMessage.error('保存失败: ' + error.message)
              })
          })
        } else {
          ElMessage.warning('请先选择文件')
        }
      }
    }


    // 在进入全屏方法中添加屏幕唤醒锁定功能
    const enterFullscreen = async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen()
          isFullscreen.value = true
          
          // 请求屏幕唤醒锁
          if ('wakeLock' in navigator) {
            try {
              wakeLock.value = await navigator.wakeLock.request('screen')
              console.log('屏幕唤醒锁已激活')
              
              // 监听可见性变化，在页面重新可见时重新请求唤醒锁
              document.addEventListener('visibilitychange', handleVisibilityChange)
            } catch (err) {
              console.error('无法获取屏幕唤醒锁:', err)
            }
          } else {
            console.warn('您的浏览器不支持屏幕唤醒锁API')
          }
        }
      } catch (err) {
        console.error('进入全屏失败:', err)
      }
    }

    


    // 处理页面可见性变化
    const handleVisibilityChange = async function() {
      try {
        // 只在页面重新变为可见时处理
        if (document.visibilityState === 'visible') {
          console.log('页面重新获得焦点，检查会话状态')
          
          // 检查是否是管理员用户
          const isAdmin = localStorage.getItem('isAdmin') === 'true';
          
          // 如果是管理员用户，使用管理员令牌检查
          if (isAdmin) {
            console.log('检测到管理员用户，跳过Supabase会话检查');
            // 为管理员进行专门的会话验证
            const adminToken = localStorage.getItem('adminToken');
            if (adminToken) {
              try {
                // 验证管理员令牌，使用项目统一的request工具
                await request({
                  url: '/admin/check',
                  method: 'get'
                });
                
                console.log('管理员令牌验证成功');
              } catch (adminTokenError) {
                console.error('管理员令牌验证失败', adminTokenError);
                // 提示管理员重新登录
                store.commit('setAdmin', { isAdmin: false, username: null });
                store.commit('setAdminToken', null);
                ElMessage.error('管理员会话已过期，请重新登录');
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUsername');
                localStorage.removeItem('isAdmin');
              }
              return;
            }
          }
          
          // 获取当前时间戳
          const now = Date.now();
          
          // 获取supabase实例
          const supabase = await getSupabase();
          
          // 尝试使用supabase检查并刷新会话
          if (supabase) {
            // 获取当前会话信息以验证令牌有效性
            const { data: { session }, error } = await supabase.auth.getSession();
            
            // 如果获取会话失败或没有会话，尝试刷新
            if (error || !session) {
              console.warn('页面重新获得焦点，但会话可能已失效');
              // 尝试刷新会话
              const { error: refreshError } = await supabase.auth.refreshSession();
              if (refreshError) {
                console.error('刷新会话失败，可能需要重新登录');
              } else {
                // 记录刷新时间
                localStorage.setItem('lastTokenRefreshTime', now.toString());
                console.log('会话刷新成功');
              }
            } else {
              console.log('页面重新获得焦点，会话有效');
              
              // 检查令牌是否即将过期
              try {
                // 解析JWT令牌
                const base64Url = session.access_token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                  return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));
                
                const { exp } = JSON.parse(jsonPayload);
                if (exp) {
                  // 计算剩余有效期
                  const expiresAt = exp * 1000;
                  const timeUntilExpiry = expiresAt - now;
                  
                  // 如果令牌即将过期（不足30分钟），刷新令牌
                  const EXPIRY_THRESHOLD = 30 * 60 * 1000; // 30分钟
                  if (timeUntilExpiry < EXPIRY_THRESHOLD) {
                    console.log(`令牌将在${Math.round(timeUntilExpiry/60000)}分钟后过期，需要刷新`);
                    // 刷新令牌
                    const { error: refreshError } = await supabase.auth.refreshSession();
                    if (refreshError) {
                      console.error('刷新令牌失败:', refreshError);
                    } else {
                      // 记录刷新时间
                      localStorage.setItem('lastTokenRefreshTime', now.toString());
                      console.log('令牌刷新成功');
                    }
                  } else {
                    console.log(`令牌有效期充足，还有${Math.round(timeUntilExpiry/60000)}分钟`);
                  }
                }
              } catch (parseError) {
                console.error('解析令牌出错:', parseError);
              }
            }
            
            // 检查权限是否正常
            try {
              const { error: permissionError } = await supabase
                .from('user_status')
                .select('id')
                .limit(1);
                
              if (permissionError && (permissionError.code === '42501' || permissionError.message?.includes('permission denied'))) {
                console.warn('权限验证失败，尝试刷新令牌');
                const { error: refreshError } = await supabase.auth.refreshSession();
                if (refreshError) {
                  console.error('刷新令牌失败:', refreshError);
                } else {
                  // 记录刷新时间
                  localStorage.setItem('lastTokenRefreshTime', now.toString());
                  console.log('令牌刷新成功');
                }
              }
            } catch (testError) {
              console.error('权限测试出错:', testError);
            }
          }
        }
      } catch (e) {
        console.error('处理页面可见性变化时出错:', e);
      }
    };
    
    // 在退出全屏方法中释放屏幕唤醒锁
    const exitFullscreen = () => {
      if (document.exitFullscreen) {
        document.exitFullscreen()
        isFullscreen.value = false
        
        // 释放屏幕唤醒锁
        if (wakeLock.value !== null) {
          wakeLock.value.release()
            .then(() => {
              console.log('屏幕唤醒锁已释放')
              wakeLock.value = null
            })
            .catch((err) => {
              console.error('释放屏幕唤醒锁失败:', err)
            })
        }
      }
    }

    watch(
      () => route.fullPath,
      () => {
        closeTransientDialogs()
        nextTick(() => {
          removeStalePopupNodes()
          resetPopupBodyState()
        })
      },
      { immediate: true }
    )

    onMounted(async () => {
      // 初始设置100%缩放
      

      

      
      // 初始化认证状态
      await store.dispatch('initializeAuth')
      
      // 获取文件列表
      await store.dispatch('fetchFiles')
      
      // 添加事件监听器
      document.addEventListener('keydown', handleKeyDown, true)
      
          })

    // 在组件卸载时移除事件监听
    onBeforeUnmount(() => {
      document.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      

      

      
      // 恢复原始缩放
      document.body.style.transform = ''
      document.body.style.transformOrigin = ''
      document.body.style.zoom = ''
      document.body.style.width = ''

      // 在组件卸载时确保释放唤醒锁
      if (wakeLock.value !== null) {
        wakeLock.value.release()
          .then(() => {
            console.log('组件卸载：屏幕唤醒锁已释放')
            wakeLock.value = null
          })
          .catch((err) => {
            console.error('组件卸载：释放屏幕唤醒锁失败:', err)
          })
      }
    })



    return {
      isCyberPage,
      isCyberLayout,
      showContact,
      showApiConfig,
      showLogin: ref(false),
      currentFile,
      aiDialogVisible,
      aiDialogTitle,
      aiDialogContent,
      enterFullscreen,
      exitFullscreen,
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: "Microsoft YaHei", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
}

.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100vw;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: #f5f5f5;
  /* 确保缩放功能正常工作 */
  transform-origin: top left;
}

/* 赛博风页面使用单独的全屏滚动区域，避免露出默认背景 */
.app-container.cyber-page {
  min-height: 100vh;
  height: auto;
  overflow-x: hidden;
  overflow-y: visible;
  background: #0a0e27;
}

/* 非赛博朋克页面保持原有的固定高度布局 */
.app-container:not(.cyber-page) {
  height: 100vh;
  overflow: hidden;
}

.contact-content {
  padding: 20px 16px;
  text-align: left;
}

.dialog-title {
  text-align: center;
  font-size: 22px;
  font-weight: 600;
  color: #409EFF;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #409EFF, #67C23A);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 1px;
  position: relative;
}

.dialog-title::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background: linear-gradient(135deg, #409EFF, #67C23A);
  border-radius: 1.5px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 16px;
}

/* 必读对话框样式 */
.read-me-dialog {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.read-me-overlay {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.read-me-overlay .el-dialog {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.read-me-dialog .el-dialog__body {
  padding: 0;
}

.info-section {
  padding: 16px 18px;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 1px solid #ebeef5;
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin: 0;
}

.info-section.feature {
  background: #f0f7ff;
  border-left: 4px solid #409EFF;
}

.info-section:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}

.info-section h3 {
  color: #409EFF;
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.info-section.feature h3::before {
  display: none;
}

.info-section p {
  margin: 0;
  color: #606266;
  line-height: 1.3;
  font-size: 14px;
  padding-left: 0;
}

.info-section.warning {
  background: #fff8f2;
  border-left: 4px solid #E6A23C;
}

.info-section.warning h3 {
  color: #E6A23C;
}

.info-section.warning p {
  color: #E6A23C;
  font-weight: 500;
  padding-left: 0;
}

.info-section.contact {
  background: #f0f9eb;
  border-left: 4px solid #67C23A;
}

.info-section.contact h3 {
  color: #67C23A;
}

.info-section.contact .email {
  color: #67C23A;
  font-weight: 500;
  font-family: monospace;
  padding-left: 0;
  font-size: 15px;
}

/* Element Plus 组件样式覆盖 */
.el-main {
  --el-main-padding: 0;
}

.el-menu {
  --el-menu-hover-bg-color: #f5f7fa;
}

.el-button {
  --el-button-hover-text-color: #409eff;
  --el-button-hover-bg-color: #ecf5ff;
}

/* 自定义滚动条样式 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 3px;
}

::-webkit-scrollbar-track {
  background: #f5f5f5;
}

/* 全局样式 */
.el-message {
  z-index: 9999 !important;
}

.el-message-box {
  z-index: 9999 !important;
}

/* 修复日期选择器层级问题 */
.date-picker-popper {
  z-index: 3100 !important;
}

.el-picker__popper {
  z-index: 3100 !important;
}

.el-popper {
  z-index: 3100 !important;
}

/* 确保弹窗层级正确 */
.el-dialog {
  margin-top: 8vh !important;
}

.el-overlay.is-align-center .el-dialog,
.read-me-overlay .el-dialog {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.el-dialog__wrapper {
  z-index: 3000 !important;
}

.el-overlay {
  z-index: 2990 !important;
}

</style>
